import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Popover } from "@base-ui/react/popover";
import { Check, MoreVertical, Search, SlidersHorizontal, X, Zap } from "lucide-react";
import { Button } from "../Button";
import { Menu } from "../Menu";
import { ShellTabs } from "./ShellTabs";

export interface ActionBarTab {
    id: string;
    label: ReactNode;
    count?: number;
}

export interface ActionBarOption {
    id: string;
    label: ReactNode;
}

/**
 * Attend-style collection toolbar. It keeps the 44px toolbar rhythm, opens one
 * feature inline on wide panes, and changes to equally sized menu/popover cells
 * when a pane is too narrow to show its tabs without clipping.
 */
export function ActionBar({
    value,
    onValueChange,
    tabs,
    ariaLabel,
    leading,
    actions,
    selection,
    search,
    onSearch,
    searchPlaceholder = "Search conversations…",
    displayMode = "comfortable",
    onDisplayMode,
    layoutMode = "list",
    onLayoutMode,
    sortMode,
    onSortMode,
    sortOptions = [],
    groupMode,
    onGroupMode,
    groupOptions = [],
}: {
    value: string;
    onValueChange: (value: string) => void;
    tabs: readonly ActionBarTab[];
    ariaLabel: string;
    leading?: ReactNode;
    actions?: ReactNode;
    selection?: { count: number; actions?: ReactNode; onClear: () => void };
    search?: string;
    onSearch?: (value: string) => void;
    searchPlaceholder?: string;
    displayMode?: "comfortable" | "compact";
    onDisplayMode?: (value: "comfortable" | "compact") => void;
    layoutMode?: "list" | "table";
    onLayoutMode?: (value: "list" | "table") => void;
    sortMode?: string;
    onSortMode?: (value: string) => void;
    sortOptions?: readonly ActionBarOption[];
    groupMode?: string;
    onGroupMode?: (value: string) => void;
    groupOptions?: readonly ActionBarOption[];
}) {
    const selecting = Boolean(selection?.count);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchPopoverOpen, setSearchPopoverOpen] = useState(false);
    const [narrow, setNarrow] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const mirrorRef = useRef<HTMLDivElement>(null);
    const leadingRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    // Ported from Attend's AAA console: measure a hidden natural-width mirror
    // instead of switching the whole bar at an arbitrary pane breakpoint.
    const remeasure = useCallback(() => {
        const root = rootRef.current;
        const mirror = mirrorRef.current;
        if (!root || !mirror || root.clientWidth === 0) return;
        const iconCount = Number(Boolean(onSearch))
            + Number(Boolean(onDisplayMode || onLayoutMode || onSortMode || onGroupMode))
            + Number(Boolean(actions));
        const required = (leadingRef.current?.offsetWidth ?? 0) + mirror.offsetWidth + iconCount * 52 + 12;
        setNarrow(root.clientWidth < required);
    }, [actions, onDisplayMode, onGroupMode, onLayoutMode, onSearch, onSortMode]);

    useLayoutEffect(() => {
        remeasure();
        const root = rootRef.current;
        if (!root || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(remeasure);
        observer.observe(root);
        if (mirrorRef.current) observer.observe(mirrorRef.current);
        return () => observer.disconnect();
    }, [remeasure, tabs]);

    function clearSearch(close = false) {
        onSearch?.("");
        if (close) {
            setSearchOpen(false);
            setSearchPopoverOpen(false);
        }
    }

    const quickViews = (triggerClassName: string) => (
        <Menu.Root>
            <Menu.Trigger render={<Button variant="ghost" size="sm" className={triggerClassName} aria-label="Quick views" />}>
                <span className="shell-action-icon-wrap"><Zap />{value !== tabs[0]?.id && <i />}</span>
            </Menu.Trigger>
            <Menu.Content align="start" className="shell-action-options-menu">
                <Menu.Group>
                    <Menu.GroupLabel>Quick views</Menu.GroupLabel>
                    {tabs.map(tab => (
                        <Menu.Item key={tab.id} onClick={() => onValueChange(tab.id)}>
                            {tab.id === value ? <Check /> : <span className="menu-icon-spacer" />}
                            <span>{tab.label}</span>
                            {tab.count != null && <small>{tab.count}</small>}
                        </Menu.Item>
                    ))}
                </Menu.Group>
            </Menu.Content>
        </Menu.Root>
    );

    const displayOptions = onDisplayMode || onLayoutMode || onSortMode || onGroupMode ? (
        <Menu.Root>
            <Menu.Trigger render={<Button variant="ghost" size="sm" className="shell-action-feature-cell" aria-label="Display options" />}>
                <SlidersHorizontal />
            </Menu.Trigger>
            <Menu.Content align="end" className="shell-action-options-menu">
                {onLayoutMode && (
                    <Menu.Group>
                        <Menu.GroupLabel>Layout</Menu.GroupLabel>
                        <Menu.Item onClick={() => onLayoutMode("list")}>
                            {layoutMode === "list" ? <Check /> : <span className="menu-icon-spacer" />}Message list
                        </Menu.Item>
                        <Menu.Item onClick={() => onLayoutMode("table")}>
                            {layoutMode === "table" ? <Check /> : <span className="menu-icon-spacer" />}Table
                        </Menu.Item>
                    </Menu.Group>
                )}
                {onLayoutMode && onDisplayMode && <Menu.Separator />}
                {onDisplayMode && (
                    <Menu.Group>
                        <Menu.GroupLabel>Row density</Menu.GroupLabel>
                        <Menu.Item onClick={() => onDisplayMode("comfortable")}>
                            {displayMode === "comfortable" ? <Check /> : <span className="menu-icon-spacer" />}Comfortable
                        </Menu.Item>
                        <Menu.Item onClick={() => onDisplayMode("compact")}>
                            {displayMode === "compact" ? <Check /> : <span className="menu-icon-spacer" />}Compact
                        </Menu.Item>
                    </Menu.Group>
                )}
                {(onDisplayMode || onLayoutMode) && onSortMode && sortOptions.length > 0 && <Menu.Separator />}
                {onSortMode && sortOptions.length > 0 && (
                    <Menu.Group>
                        <Menu.GroupLabel>Sort by</Menu.GroupLabel>
                        {sortOptions.map(option => (
                            <Menu.Item key={option.id} onClick={() => onSortMode(option.id)}>
                                {sortMode === option.id ? <Check /> : <span className="menu-icon-spacer" />}{option.label}
                            </Menu.Item>
                        ))}
                    </Menu.Group>
                )}
                {(onDisplayMode || onLayoutMode || (onSortMode && sortOptions.length > 0)) && onGroupMode && groupOptions.length > 0 && <Menu.Separator />}
                {onGroupMode && groupOptions.length > 0 && (
                    <Menu.Group>
                        <Menu.GroupLabel>Group by</Menu.GroupLabel>
                        {groupOptions.map(option => (
                            <Menu.Item key={option.id} onClick={() => onGroupMode(option.id)}>
                                {groupMode === option.id ? <Check /> : <span className="menu-icon-spacer" />}{option.label}
                            </Menu.Item>
                        ))}
                    </Menu.Group>
                )}
            </Menu.Content>
        </Menu.Root>
    ) : null;

    const moreActions = actions ? (
        <Menu.Root>
            <Menu.Trigger render={<Button variant="ghost" size="sm" className="shell-action-feature-cell" aria-label="More actions" />}>
                <MoreVertical />
            </Menu.Trigger>
            <Menu.Content align="end"><div className="shell-action-menu-content">{actions}</div></Menu.Content>
        </Menu.Root>
    ) : null;

    return (
        <div ref={rootRef} className={`shell-action-bar${selecting ? " is-selecting" : ""}${narrow ? " is-narrow" : ""}`}>
            <div ref={mirrorRef} className="shell-action-tabs-mirror" aria-hidden="true">
                <ShellTabs value={value} onValueChange={() => {}} tabs={tabs} ariaLabel="" />
            </div>
            {leading && <div ref={leadingRef} className="shell-action-leading">{leading}</div>}
            {selecting ? (
                <>
                    <div className="shell-action-spacer" />
                    <div className="shell-action-controls">{selection!.actions}</div>
                    <Button variant="ghost" size="sm" onClick={selection!.onClear} aria-label="Clear selection"><X /></Button>
                </>
            ) : (
                <>
                    <div className="shell-action-narrow-cells">
                        {quickViews("shell-action-feature-cell")}
                        {onSearch && (
                            <Popover.Root open={searchPopoverOpen} onOpenChange={setSearchPopoverOpen}>
                                <Popover.Trigger render={<Button variant="ghost" size="sm" className="shell-action-feature-cell" aria-label="Search" />}>
                                    <span className="shell-action-icon-wrap"><Search />{Boolean(search?.trim()) && <i />}</span>
                                </Popover.Trigger>
                                <Popover.Portal>
                                    <Popover.Positioner sideOffset={4} align="center" collisionPadding={8} className="shell-search-positioner">
                                        <Popover.Popup className="shell-search-popover">
                                            <Search aria-hidden="true" />
                                            <input
                                                autoFocus
                                                type="search"
                                                value={search ?? ""}
                                                onChange={event => onSearch(event.target.value)}
                                                onKeyDown={event => event.key === "Escape" && setSearchPopoverOpen(false)}
                                                placeholder={searchPlaceholder}
                                                aria-label={searchPlaceholder.replace(/…$/, "")}
                                            />
                                            {search && <Button variant="ghost" size="sm" onClick={() => clearSearch()} aria-label="Clear search"><X /></Button>}
                                        </Popover.Popup>
                                    </Popover.Positioner>
                                </Popover.Portal>
                            </Popover.Root>
                        )}
                        {displayOptions}
                        {moreActions}
                    </div>
                    <div className="shell-action-wide-content">
                        {searchOpen && onSearch ? (
                            <>
                                <Button variant="ghost" size="sm" className="shell-action-feature-cell shell-view-toggle" onClick={() => setSearchOpen(false)} aria-label="Show quick views">
                                    <span className="shell-action-icon-wrap"><Zap />{value !== tabs[0]?.id && <i />}</span>
                                </Button>
                                <div className="shell-action-search">
                                    <Search aria-hidden="true" />
                                    <input
                                        ref={searchRef}
                                        autoFocus
                                        type="search"
                                        value={search ?? ""}
                                        onChange={event => onSearch(event.target.value)}
                                        onKeyDown={event => event.key === "Escape" && setSearchOpen(false)}
                                        placeholder={searchPlaceholder}
                                        aria-label={searchPlaceholder.replace(/…$/, "")}
                                    />
                                    {search && <Button variant="ghost" size="sm" onClick={() => clearSearch()} aria-label="Clear search"><X /></Button>}
                                </div>
                            </>
                        ) : (
                            <ShellTabs value={value} onValueChange={onValueChange} tabs={tabs} ariaLabel={ariaLabel} />
                        )}
                        <div className={`shell-action-spacer${searchOpen && onSearch ? " is-search-open" : ""}`} />
                        <div className="shell-action-cells" aria-label="Table tools">
                            {onSearch && !searchOpen && <Button variant="ghost" size="sm" className="shell-action-feature-cell" aria-label="Search" onClick={() => setSearchOpen(true)} data-active={Boolean(search)}><Search /></Button>}
                            {displayOptions}
                            {moreActions}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
